import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { GatewayInstance } from '@vsolv/packages/payments/domain';
import { PaymentGatewayInstanceService } from '@vsolv/packages/payments/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { BadgeColumn, ButtonColumn, IconButtonColumn, TextColumn } from '@vsolv/vectors/table';
import { SecurityService } from '@wsphere/staff/web';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  from,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { GatewayInstanceDialog } from '../../dialogs/gateway-instance-dialog';

type GatewayLifecycleAction = 'activate' | 'runoff' | 'disable';

const STATUS_THEME: Record<GatewayInstance.Status, ThemeColor> = {
  [GatewayInstance.Status.ACTIVE]: 'success',
  [GatewayInstance.Status.PENDING_VERIFICATION]: 'default',
  [GatewayInstance.Status.RUNOFF]: 'warn',
  [GatewayInstance.Status.DISABLED]: 'danger',
};

const STATUS_LABEL: Record<GatewayInstance.Status, string> = {
  [GatewayInstance.Status.ACTIVE]: 'Active',
  [GatewayInstance.Status.PENDING_VERIFICATION]: 'Pending verification',
  [GatewayInstance.Status.RUNOFF]: 'Runoff',
  [GatewayInstance.Status.DISABLED]: 'Disabled',
};

const PROVIDER_LABEL: Record<GatewayInstance.Provider, string> = {
  [GatewayInstance.Provider.AUTHORIZE_NET]: 'Authorize.Net',
};

const PLATFORM_DISABLED_MESSAGE =
  'This VectorSolv payment connection is disabled. Contact a VectorSolv administrator to enable it. Existing assigned payments can continue on Active or Runoff gateways.';

const PLATFORM_MANAGED_MESSAGE =
  'Authorize.Net credentials for this connection are managed by VectorSolv. Contact a VectorSolv administrator to make changes.';

interface GatewayListState {
  gatewayInstances: GatewayInstance.Model[];
  loading: boolean;
  error: string | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-gateway-instances-table',
  templateUrl: './gateway-instances-table.component.html',
  standalone: false,
})
export class GatewayInstancesTableComponent {
  constructor(
    private gatewaySvc: PaymentGatewayInstanceService,
    private securitySvc: SecurityService,
    private toastSvc: ToastService
  ) {}

  private refresh$ = new BehaviorSubject<null>(null);

  @Input() newSalesBlockedReason: string | null = null;
  @Output() activeGatewaysChanged = new EventEmitter<boolean>();

  @ViewChild('lifecycleDialog') lifecycleDialog?: DialogComponent;
  @ViewChild('gatewayDialog') gatewayDialog?: GatewayInstanceDialog;

  selectedGateway: GatewayInstance.Model | null = null;
  lifecycleAction: GatewayLifecycleAction | null = null;
  submittingLifecycleChange = false;
  canManageGatewayInstances = false;

  canManageGatewayInstances$ = from(this.securitySvc.hasAccess('stf_ManageAuthNetApp', null)).pipe(
    catchError(() => of(false)),
    tap(allowed => (this.canManageGatewayInstances = allowed)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  gatewayState$ = combineLatest([this.refresh$, this.canManageGatewayInstances$]).pipe(
    switchMap(([, canManageGatewayInstances]): Observable<GatewayListState> => {
      if (!canManageGatewayInstances) {
        return of({ gatewayInstances: [], loading: false, error: null });
      }

      return from(this.gatewaySvc.list()).pipe(
        map(gatewayInstances => {
          this.activeGatewaysChanged.emit(
            gatewayInstances.some(
              instance =>
                instance.status === GatewayInstance.Status.ACTIVE && instance.platformConnectionEnabled !== false
            )
          );
          return { gatewayInstances, loading: false, error: null };
        }),
        catchError((error: unknown) =>
          of({
            gatewayInstances: [],
            loading: false,
            error: this.getErrorMessage(error, 'Unable to load payment gateway instances.'),
          })
        ),
        startWith({ gatewayInstances: [], loading: true, error: null })
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  gatewayInstances$ = this.gatewayState$.pipe(
    filter(state => !state.loading),
    map(state => state.gatewayInstances)
  );

  data$ = combineLatest([this.gatewayState$, this.canManageGatewayInstances$]).pipe(
    map(([state, canManageGatewayInstances]) => ({ ...state, canManageGatewayInstances }))
  );

  columns$ = this.canManageGatewayInstances$.pipe(
    map(canManageGatewayInstances => [
      new TextColumn<GatewayInstance.Model>({ header: 'Name' }, item => ({ text: item.name })),
      new TextColumn<GatewayInstance.Model>({ header: 'Provider' }, item => ({ text: PROVIDER_LABEL[item.provider] })),
      new BadgeColumn<GatewayInstance.Model>({ header: 'Status' }, item => ({
        text:
          item.platformConnectionEnabled === false && item.status === GatewayInstance.Status.ACTIVE
            ? 'Disabled'
            : STATUS_LABEL[item.status],
        theme:
          item.platformConnectionEnabled === false && item.status === GatewayInstance.Status.ACTIVE
            ? 'warn'
            : STATUS_THEME[item.status],
        tooltipText: item.platformConnectionEnabled === false ? PLATFORM_DISABLED_MESSAGE : undefined,
      })),
      new BadgeColumn<GatewayInstance.Model>({ header: 'Default' }, item =>
        item.platformConnectionEnabled === false
          ? { text: 'Unavailable for new sales', theme: 'default', tooltipText: PLATFORM_DISABLED_MESSAGE }
          : item.isDefault
            ? { text: 'Default', theme: 'success' }
            : {
                buttonText: 'Set as default',
                buttonTheme: 'default',
                disabled:
                  !canManageGatewayInstances ||
                  item.status !== GatewayInstance.Status.ACTIVE ||
                  !!this.newSalesBlockedReason,
                buttonClick: () => this.setDefault(item),
              }
      ),
      ...(canManageGatewayInstances
        ? [
            new ButtonColumn<GatewayInstance.Model>({ header: 'Actions', fitContent: true }, item => {
              if (item.platformConnectionEnabled === false) {
                if (item.status === GatewayInstance.Status.DISABLED) return {};
                return {
                  text: 'Enable',
                  type: 'outline',
                  theme: 'default',
                  disabled: true,
                  tooltip: { content: PLATFORM_DISABLED_MESSAGE },
                };
              }
              if (item.status === GatewayInstance.Status.RUNOFF) {
                return {
                  text: 'Reactivate',
                  type: 'outline',
                  theme: 'success',
                  disabled: !!this.newSalesBlockedReason,
                  click: () => this.openLifecycleConfirmation(item, 'activate'),
                };
              }
              if (item.status === GatewayInstance.Status.ACTIVE) {
                return {
                  text: 'Move to runoff',
                  type: 'outline',
                  theme: 'warn',
                  disabled: item.isDefault === true,
                  tooltip: item.isDefault
                    ? {
                        content: 'Select another active default gateway before moving this gateway to runoff.',
                      }
                    : undefined,
                  click: () => this.openLifecycleConfirmation(item, 'runoff'),
                };
              }
              return {};
            }),
            new ButtonColumn<GatewayInstance.Model>({ fitContent: true }, item =>
              item.platformConnectionEnabled !== false &&
              [
                GatewayInstance.Status.ACTIVE,
                GatewayInstance.Status.RUNOFF,
                GatewayInstance.Status.PENDING_VERIFICATION,
              ].includes(item.status)
                ? {
                    text: 'Disable',
                    type: 'outline',
                    theme: 'danger',
                    disabled: item.isDefault === true,
                    tooltip: item.isDefault
                      ? { content: 'Select another active default gateway before disabling this gateway.' }
                      : undefined,
                    click: () => this.openLifecycleConfirmation(item, 'disable'),
                  }
                : {}
            ),
            new IconButtonColumn<GatewayInstance.Model>({ stickyEnd: true, fitContent: true }, item => ({
              type: 'clear',
              rounded: true,
              icon: 'edit-01',
              disabled: item.platformConnectionEnabled !== undefined,
              tooltipText:
                item.platformConnectionEnabled !== undefined ? PLATFORM_MANAGED_MESSAGE : `Edit ${item.name}`,
              click: () => this.openEditDialog(item),
            })),
          ]
        : []),
    ])
  );

  get lifecycleDialogTitle(): string {
    if (this.lifecycleAction === 'activate') return 'Reactivate gateway';
    if (this.lifecycleAction === 'runoff') return 'Move gateway to runoff';
    return 'Disable gateway';
  }

  get lifecycleDialogDescription(): string {
    if (this.lifecycleAction === 'activate') {
      return 'Validates the saved credentials before making this gateway available for new sales again.';
    }
    if (this.lifecycleAction === 'runoff') {
      return 'Stops new sales; existing installments, retries, and refunds continue on this gateway.';
    }
    return 'Stops all provider processing for this gateway, including refunds, retries, and renewals for existing payments assigned to it. This action is permanent and cannot be undone. Use Runoff if existing payments or obligations still require this account.';
  }

  get lifecycleDialogIcon(): 'refresh-ccw-04' | 'clock-rewind' | 'alert-triangle' {
    if (this.lifecycleAction === 'activate') return 'refresh-ccw-04';
    return this.lifecycleAction === 'runoff' ? 'clock-rewind' : 'alert-triangle';
  }

  get lifecycleDialogTheme(): ThemeColor {
    if (this.lifecycleAction === 'activate') return 'success';
    return this.lifecycleAction === 'runoff' ? 'warn' : 'danger';
  }

  get lifecycleConfirmText(): string {
    if (this.lifecycleAction === 'activate') return 'Reactivate';
    return this.lifecycleAction === 'runoff' ? 'Move to runoff' : 'Disable gateway';
  }
  openCreateDialog(): void {
    if (!this.canManageGatewayInstances || this.newSalesBlockedReason) return;
    this.gatewayDialog?.openCreate();
  }

  openEditDialog(item: GatewayInstance.Model): void {
    if (!this.canManageGatewayInstances || item.platformConnectionEnabled !== undefined) return;
    void this.gatewayDialog?.openEdit(item.id);
  }

  onGatewaySaved(): void {
    this.refreshTable();
  }

  retryLoad(): void {
    this.refreshTable();
  }

  refreshTable(): void {
    this.refresh$.next(null);
  }

  openLifecycleConfirmation(item: GatewayInstance.Model, action: GatewayLifecycleAction) {
    if (!this.canManageGatewayInstances || item.platformConnectionEnabled === false) return;
    this.selectedGateway = item;
    this.lifecycleAction = action;
    this.lifecycleDialog?.open();
  }

  closeLifecycleConfirmation() {
    if (this.submittingLifecycleChange) return;
    this.lifecycleDialog?.close();
    this.selectedGateway = null;
    this.lifecycleAction = null;
  }

  async confirmLifecycleChange() {
    if (!this.canManageGatewayInstances) return;
    if (!this.selectedGateway || !this.lifecycleAction || this.submittingLifecycleChange) return;
    if (this.selectedGateway.platformConnectionEnabled === false) return;
    if (this.lifecycleAction === 'activate' && this.newSalesBlockedReason) return;

    const gateway = this.selectedGateway;
    const action = this.lifecycleAction;
    this.submittingLifecycleChange = true;

    try {
      const updated = await this.applyLifecycleAction(gateway.id, action);
      this.toastSvc.show({
        type: 'success',
        title:
          action === 'activate'
            ? 'Gateway reactivated'
            : action === 'runoff'
              ? 'Gateway moved to runoff'
              : 'Gateway disabled',
        text: `${gateway.name} is now ${updated.status.toLowerCase()}.`,
      });
      this.submittingLifecycleChange = false;
      this.closeLifecycleConfirmation();
      this.refreshTable();
    } catch (error: unknown) {
      this.toastSvc.show({
        type: 'error',
        title: 'Gateway update failed',
        text: this.getErrorMessage(
          error,
          action === 'activate'
            ? 'Unable to reactivate this gateway.'
            : `Unable to ${action === 'runoff' ? 'move' : 'disable'} this gateway.`
        ),
      });
      this.submittingLifecycleChange = false;
    }
  }

  private applyLifecycleAction(id: string, action: GatewayLifecycleAction): Promise<GatewayInstance.Model> {
    if (action === 'activate') return this.gatewaySvc.activate(id);
    if (action === 'runoff') return this.gatewaySvc.moveToRunoff(id);
    return this.gatewaySvc.disable(id);
  }

  private async setDefault(item: GatewayInstance.Model) {
    if (!this.canManageGatewayInstances || this.newSalesBlockedReason || item.platformConnectionEnabled === false)
      return;
    try {
      await this.gatewaySvc.setDefault(item.id);
      this.toastSvc.show({
        type: 'success',
        title: 'Default gateway updated',
        text: `${item.name} is now the default gateway for new sales.`,
      });
      this.refreshTable();
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      this.toastSvc.show({
        type: 'error',
        title: 'Error',
        text: err?.error?.message ?? 'Unable to set this gateway as the default.',
      });
    }
  }

  private getErrorMessage(error: unknown, fallback: string) {
    const message = (error as { error?: { message?: string | string[] } })?.error?.message;
    if (Array.isArray(message)) return message.join(', ');
    return message ?? fallback;
  }
}
